import type { ExtractionInfo, FileInfo } from "../dealTypes";
import Box from "@/ui/atoms/Box";
import Typography from "@/ui/atoms/Typography";
import { Modal, Button } from "@proda-ai/ui-kit";

type DeleteConfirmationModalProps = {
    showModal: boolean;
    fileInfo?: FileInfo[];
    extractionInfo?: ExtractionInfo[];
    onConfirm: () => void;
    onCancel: () => void;
    isConfirmLoading?: boolean;
    confirmLoadingText?: string;
};

export const DeleteConfirmationModal = ({
    showModal,
    fileInfo,
    extractionInfo,
    onConfirm,
    onCancel,
    isConfirmLoading = false,
    confirmLoadingText = "Loading...",
}: DeleteConfirmationModalProps) => {
    const modalTitle =
        "Are you sure you want to delete the selected files and extractions?";

    const renderFileExtractionList = () => {
        return (
            <Box sx={{ marginBottom: "1rem" }}>
                <Typography variant="body1">
                    These files and extractions will be deleted:
                </Typography>
                <Box
                    component="ul"
                    sx={{ listStyleType: "disc", paddingLeft: "30px" }}
                >
                    {fileInfo?.map(file => (
                        <Box component="li" key={file.fileId}>
                            <Typography variant="body2">
                                {file.originalFilename}
                            </Typography>
                            {extractionInfo &&
                                extractionInfo.some(
                                    extraction =>
                                        extraction.fileId === file.fileId,
                                ) && (
                                    <Box
                                        component="ul"
                                        sx={{
                                            listStyleType: "circle",
                                            paddingLeft: "20px",
                                        }}
                                    >
                                        {extractionInfo
                                            .filter(
                                                extraction =>
                                                    extraction.fileId ===
                                                    file.fileId,
                                            )
                                            .map(extraction => (
                                                <Box
                                                    component="li"
                                                    key={
                                                        extraction.extractionId
                                                    }
                                                >
                                                    <Typography variant="body2">
                                                        {`Extraction: ${extraction.sheetName} Table ${extraction.extractionId}`}
                                                    </Typography>
                                                </Box>
                                            ))}
                                    </Box>
                                )}
                        </Box>
                    ))}
                    {extractionInfo
                        ?.filter(
                            extraction =>
                                fileInfo?.some(
                                    file => file.fileId === extraction.fileId,
                                ) === false,
                        )
                        .map(extraction => (
                            <Box component="li" key={extraction.extractionId}>
                                <Typography variant="body2">
                                    {`Extraction: ${extraction.sheetName} Table ${extraction.extractionId}`}
                                </Typography>
                            </Box>
                        ))}
                </Box>
            </Box>
        );
    };

    const renderFooter = () => (
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
            <Button
                onClick={onCancel}
                pattern="secondary"
                size="medium"
                disabled={isConfirmLoading}
            >
                Cancel
            </Button>
            <Button
                onClick={onConfirm}
                pattern="primary"
                size="medium"
                isLoading={isConfirmLoading}
                loadingText={confirmLoadingText}
                disabled={isConfirmLoading}
            >
                Confirm
            </Button>
        </Box>
    );

    return (
        <Modal
            open={showModal}
            headerTitle={modalTitle}
            onCancel={onCancel}
            onClose={onCancel}
            footer={renderFooter()}
        >
            {renderFileExtractionList()}
        </Modal>
    );
};
